import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './LogDuck.css';

interface ShipOption {
  id: number;
  name: string;
  description?: string;
}

const LogDuck: React.FC = () => {
  const [ships, setShips] = useState<ShipOption[]>([]);
  const [shipError, setShipError] = useState('');
  const [shipLoading, setShipLoading] = useState(false);
  const [selectedShipId, setSelectedShipId] = useState<number | ''>('');
  const [location, setLocation] = useState('');
  const [foundDate, setFoundDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorInfo, setErrorInfo] = useState<{
    message: string;
    details?: string;
    reference?: number;
  } | null>(null);
  const navigate = useNavigate();
  const [hiderId, setHiderId] = useState<number | null>(null);
  const [hiderUsername, setHiderUsername] = useState<string | null>(null);

  // hider_id aus URL lesen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hiderIdParam = params.get('hider_id');
    if (hiderIdParam) {
      const id = parseInt(hiderIdParam, 10);
      if (!isNaN(id) && id > 0) {
        setHiderId(id);
        // Verstecker-Username laden
        api.get(`/users/${id}`)
          .then((response) => {
            setHiderUsername(response.data.user.username);
          })
          .catch(() => {
            // Fehler ignorieren, hider_id wird trotzdem gesendet
          });
      }
    }
  }, []);

  const loadShips = async () => {
    try {
      setShipLoading(true);
      setShipError('');
      const response = await api.get('/ships');
      const list: ShipOption[] = response.data.ships ?? [];
      setShips(list);
      if (list.length > 0 && selectedShipId === '') {
        setSelectedShipId(list[0].id);
      }
    } catch (err: any) {
      setShipError(err.response?.data?.error || 'Schiffliste konnte nicht geladen werden.');
    } finally {
      setShipLoading(false);
    }
  };

  useEffect(() => {
    loadShips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);
      setErrorInfo(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorInfo(null);
    setLoading(true);

    if (!image) {
      setErrorInfo({
        message: 'Bitte füge ein Foto deiner gefundenen Ente hinzu.',
      });
      setLoading(false);
      return;
    }

    const selectedShip = ships.find((ship) => ship.id === selectedShipId);
    if (!selectedShip) {
      setErrorInfo({
        message: 'Bitte wähle ein gültiges Schiff aus der Liste aus.',
      });
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('ship_name', selectedShip.name);
      formData.append('found_date', foundDate);
      if (location) formData.append('location', location);
      if (notes) formData.append('notes', notes);
      if (hiderId) formData.append('hider_id', String(hiderId));
      formData.append('image', image);

      await api.post('/ducks/log', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      navigate('/dashboard');
    } catch (err: any) {
      setErrorInfo({
        message: err.response?.data?.error || 'Fehler beim Loggen der Ente',
        details: err.response?.data?.details,
        reference: err.response?.data?.reference,
      });
    } finally {
      setLoading(false);
    }
  };

  const shipOptions = useMemo(
    () => [...ships].sort((a, b) => a.name.localeCompare(b.name)),
    [ships]
  );

  return (
    <div className="log-duck-container">
      <div className="log-duck-card glass">
        <h1>🦆 Ente loggen</h1>
        {hiderUsername && (
          <div className="hider-notice" style={{
            background: 'rgba(107, 91, 255, 0.15)',
            border: '1px solid rgba(107, 91, 255, 0.3)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0, color: 'var(--accent)', fontWeight: 600 }}>
              🎁 Diese Ente wurde von <strong>{hiderUsername}</strong> versteckt!
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              Der Verstecker wird benachrichtigt, sobald du die Ente loggst.
            </p>
          </div>
        )}
        {errorInfo && (
          <div className="error-message">
            <p>{errorInfo.message}</p>
            {errorInfo.details && <p>Details: {errorInfo.details}</p>}
            {errorInfo.reference && <p>Fehler-ID: {errorInfo.reference}</p>}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="shipName">Schiffsname *</label>
              {shipError && <p className="field-hint error">{shipError}</p>}
              <div className="ship-select">
                <select
                  id="shipName"
                  value={selectedShipId}
                  onChange={(e) => setSelectedShipId(e.target.value ? Number(e.target.value) : '')}
                  disabled={shipLoading || shipOptions.length === 0}
                  required
                >
                  <option value="">
                    {shipLoading ? 'Lade...' : shipOptions.length === 0 ? 'Keine Schiffe verfügbar' : 'Bitte wählen'}
                  </option>
                  {shipOptions.map((ship) => (
                    <option key={ship.id} value={ship.id}>
                      {ship.name}
                    </option>
                  ))}
                </select>
                <button type="button" className="ghost-button" onClick={loadShips} disabled={shipLoading}>
                  Aktualisieren
                </button>
              </div>
              {selectedShipId && (
                <p className="field-hint">
                  {shipOptions.find((s) => s.id === selectedShipId)?.description || 'Vordefinierter Schiffsname'}
                </p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="location">Ort/Location</label>
              <input
                type="text"
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="z.B. Deck 12, Pool-Bereich"
              />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="foundDate">Funddatum *</label>
              <input
                type="date"
                id="foundDate"
                value={foundDate}
                onChange={(e) => setFoundDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="image">Bild der Ente *</label>
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageChange}
                required
              />
              {preview && (
                <img src={preview} alt="Vorschau" className="image-preview" />
              )}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="notes">Notizen</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Zusätzliche Informationen über die Ente..."
            />
          </div>
          <button type="submit" disabled={loading} className="button-primary">
            {loading ? 'Wird geloggt...' : 'Ente loggen'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LogDuck;

